import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  Stack,
  CircularProgress,
  Divider,
  LinearProgress,
  Avatar,
  InputAdornment,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';

const ArchiveBrowser = ({
  periods,
  selectedPeriod,
  onSelectPeriod,
  archivedItems,
  archivedItemsCount,
  onRestoreItem,
  onSearch,
  isLoadingPeriods,
  isLoadingItems,
  isRestoring,
  quota,
  isLoadingQuota,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch?.(query);
  };

  const formatPeriod = (year, month) => {
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    // Handle Firestore timestamps ({ _seconds, _nanoseconds })
    const ms = typeof dateValue === 'object' && dateValue._seconds
      ? dateValue._seconds * 1000
      : new Date(dateValue).getTime();
    const date = new Date(ms);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Archive Quota Display */}
      {quota && (
        <Card
          sx={{
            mb: 3,
            backgroundColor: '#fafafa',
            borderLeft: '4px solid #1565c0',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                Archive Quota (This Month)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {quota.used} of {quota.limit} archived
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={quota.limit ? Math.min((quota.used / quota.limit) * 100, 100) : 0}
              sx={{ height: 6, borderRadius: 3 }}
              color={
                quota.limit && (quota.used / quota.limit) >= 0.9
                  ? 'error'
                  : quota.limit && (quota.used / quota.limit) >= 0.75
                    ? 'warning'
                    : 'primary'
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {quota.remaining} remaining this month
            </Typography>
          </CardContent>
        </Card>
      )}
      {isLoadingQuota && !quota && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Period List Sidebar */}
        <Box sx={{ minWidth: { md: 240 }, flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#666' }}>
            Archive Periods
          </Typography>
          {isLoadingPeriods ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : periods.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                border: '1px dashed #ccc',
              }}
            >
              <ArchiveIcon sx={{ fontSize: 36, color: '#ccc', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No archived items yet
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1}>
              {periods.map((period) => {
                const isSelected =
                  selectedPeriod?.year === period.year && selectedPeriod?.month === period.month;
                return (
                  <Paper
                    key={`${period.year}-${period.month}`}
                    elevation={isSelected ? 3 : 0}
                    onClick={() => onSelectPeriod(period)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e3f2fd' : '#fafafa',
                      border: isSelected ? '1px solid #1565c0' : '1px solid #eee',
                      borderRadius: 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: isSelected ? '#e3f2fd' : '#f0f0f0',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                      {formatPeriod(period.year, period.month)}
                    </Typography>
                    <Chip
                      label={`${period.itemCount} item${period.itemCount !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* Items List */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selectedPeriod ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {formatPeriod(selectedPeriod.year, selectedPeriod.month)}
                </Typography>
                <Chip
                  label={`${archivedItemsCount} item${archivedItemsCount !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <TextField
                  size="small"
                  placeholder="Search archived items..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ ml: 'auto', minWidth: 220 }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              {isLoadingItems ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : archivedItems.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    border: '1px dashed #ccc',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No items match your search' : 'No archived items for this period'}
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>List Price</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Archived</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {archivedItems.map((item) => (
                        <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                          <TableCell>
                            {item.mainImageUrl ? (
                              <Avatar
                                src={item.mainImageUrl}
                                alt={item.itemTitle}
                                variant="rounded"
                                sx={{ width: 40, height: 40 }}
                              />
                            ) : (
                              <Avatar
                                variant="rounded"
                                sx={{ width: 40, height: 40, backgroundColor: '#f5f5f5' }}
                              >
                                <ImageIcon sx={{ color: '#bdbdbd', fontSize: 20 }} />
                              </Avatar>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.itemTitle}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {item.salesLocation || ''}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                              ${parseFloat(item.listPrice).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(item.dateArchived)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={isRestoring ? <CircularProgress size={14} /> : <RestoreIcon />}
                              onClick={() => onRestoreItem(item)}
                              disabled={isRestoring}
                            >
                              Restore
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                border: '1px dashed #ccc',
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ArchiveIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Select a period to view archived items
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ArchiveBrowser;
